import { isMemoryArchiveType } from "@/lib/leaderMemoryVisibility";
import { format, subDays } from "date-fns";

export type ThroughlineNodeState = "healthy" | "stalled" | "risk";

export type ThroughlineMilestoneInput = {
  id: string;
  at: string;
  type: "transcript" | "reflection" | "conversation" | "veera";
  detail: string;
  link?: string | null;
};

export type ThroughlineTranscriptStats = {
  total: number;
  withSummary: number;
};

export type ThroughlineLedgerEventInput = {
  id: string;
  at: string;
  eventType: string;
  sourceType: string;
  actorRole?: string | null;
  confidenceLevel?: string | null;
};

export type ThroughlineArcPoint = {
  id: string;
  x: number;
  y: number;
  progress: number;
  evidence: number;
  agreement: number;
  intentSignal: number;
  behaviorSignal: number;
  alignment: "aligned" | "partial" | "gap";
  momentum: number;
  state: ThroughlineNodeState;
  label: string;
  detail: string;
  count: number;
  isEmpty: boolean;
};

export type ThroughlineBuildResult = {
  points: ThroughlineArcPoint[];
  effectiveWindowDays: 15 | 30 | 60 | 90;
  sparseWindowAutoExpanded: boolean;
  stateCounts: Record<ThroughlineNodeState, number>;
};

export type ThroughlineDayNarrativeInput = {
  state: ThroughlineNodeState;
  alignment: ThroughlineArcPoint["alignment"];
  count: number;
  evidence: number;
  agreement: number;
  momentum: number;
  topBehaviors: string[];
  sourceCounts: Record<ThroughlineMilestoneInput["type"], number>;
  isEmpty: boolean;
};

export type ThroughlineDayNarrative = {
  headline: string;
  summary: string;
  drivers: string[];
};

export type ThroughlineGapAlert = {
  active: boolean;
  recentWindowSize: number;
  gapDays: number;
  lowEvidenceDays: number;
};

export function describeThroughlineConfidence(params: {
  confidence: "high" | "medium" | "low";
  count: number;
  evidence: number;
}) {
  const evidencePct = Math.round(params.evidence * 100);
  if (params.confidence === "high") {
    return `Confidence is high from ${params.count} events and ${evidencePct}% evidence strength.`;
  }
  if (params.confidence === "medium") {
    return `Confidence is medium from ${params.count} events and ${evidencePct}% evidence strength.`;
  }
  return `Confidence is low because event count (${params.count}) or evidence strength (${evidencePct}%) is limited.`;
}

export const THROUGHLINE_STATE_DEFINITIONS: Record<ThroughlineNodeState, { label: string; description: string }> = {
  healthy: {
    label: "On Track",
    description: "Evidence and coach-leader alignment are strong and currently moving in a helpful direction.",
  },
  stalled: {
    label: "Needs Consistency",
    description: "Some evidence exists, but consistency or alignment is flattening and needs reinforcement.",
  },
  risk: {
    label: "Needs Attention",
    description: "Evidence or alignment is low, or recent trend direction suggests possible drift.",
  },
};

export const THROUGHLINE_RISK_RULES = {
  evidenceLt: 0.45,
  agreementLt: 0.45,
  momentumLt: -0.12,
} as const;

export const THROUGHLINE_HEALTHY_RULES = {
  evidenceGte: 0.68,
  agreementGte: 0.68,
  momentumGte: -0.05,
} as const;

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function classifyState(evidence: number, agreement: number, momentum: number): ThroughlineNodeState {
  if (
    evidence < THROUGHLINE_RISK_RULES.evidenceLt ||
    agreement < THROUGHLINE_RISK_RULES.agreementLt ||
    momentum < THROUGHLINE_RISK_RULES.momentumLt
  ) {
    return "risk";
  }
  if (
    evidence >= THROUGHLINE_HEALTHY_RULES.evidenceGte &&
    agreement >= THROUGHLINE_HEALTHY_RULES.agreementGte &&
    momentum >= THROUGHLINE_HEALTHY_RULES.momentumGte
  ) {
    return "healthy";
  }
  return "stalled";
}

export function buildThroughlineArcPoints(params: {
  milestones: ThroughlineMilestoneInput[];
  ledgerEvents?: ThroughlineLedgerEventInput[];
  transcriptStats: ThroughlineTranscriptStats;
  requestedWindowDays: 15 | 30 | 60 | 90;
  now?: Date;
}): ThroughlineBuildResult {
  const { milestones, ledgerEvents = [], transcriptStats, requestedWindowDays } = params;
  if (!milestones.length) {
    return {
      points: [],
      effectiveWindowDays: requestedWindowDays,
      sparseWindowAutoExpanded: false,
      stateCounts: { healthy: 0, stalled: 0, risk: 0 },
    };
  }

  const now = params.now ?? new Date();
  const eventsInLast15Days = milestones.filter(
    (item) => new Date(item.at).getTime() >= subDays(now, 14).getTime(),
  ).length;

  const sparseWindowAutoExpanded = requestedWindowDays === 15 && eventsInLast15Days < 4;
  const effectiveWindowDays = (sparseWindowAutoExpanded ? 30 : requestedWindowDays) as 15 | 30 | 60 | 90;

  const groupedByDay = new Map<string, ThroughlineMilestoneInput[]>();
  milestones.forEach((item) => {
    const dayKey = format(new Date(item.at), "yyyy-MM-dd");
    const existing = groupedByDay.get(dayKey) || [];
    existing.push(item);
    groupedByDay.set(dayKey, existing);
  });

  const ledgerByDay = new Map<string, ThroughlineLedgerEventInput[]>();
  ledgerEvents.filter(event => !isMemoryArchiveType(event.sourceType)).forEach((event) => {
    const dayKey = format(new Date(event.at), "yyyy-MM-dd");
    const existing = ledgerByDay.get(dayKey) || [];
    existing.push(event);
    ledgerByDay.set(dayKey, existing);
  });

  const dayBuckets = Array.from({ length: effectiveWindowDays }).map((_, index) => {
    const day = subDays(now, effectiveWindowDays - 1 - index);
    const dayKey = format(day, "yyyy-MM-dd");
    return {
      dayKey,
      items: groupedByDay.get(dayKey) || [],
    };
  });

  const summaryCoverage = transcriptStats.total > 0 ? transcriptStats.withSummary / transcriptStats.total : 0;
  let previousScore = 0.5;

  const points = dayBuckets.map((bucket, index) => {
    const progress = dayBuckets.length === 1 ? 0.5 : index / (dayBuckets.length - 1);

    const transcriptCount = bucket.items.filter((item) => item.type === "transcript").length;
    const reflectionCount = bucket.items.filter((item) => item.type === "reflection").length;
    const conversationCount = bucket.items.filter((item) => item.type === "conversation").length;
    const veeraCount = bucket.items.filter((item) => item.type === "veera").length;
    const ledgerItems = ledgerByDay.get(bucket.dayKey) || [];
    const ledgerBehaviorSignals = ledgerItems.filter(
      (item) =>
        item.sourceType.includes("transcript") ||
        item.sourceType.includes("practice") ||
        item.sourceType.includes("assessment") ||
        item.eventType.includes("analysis"),
    ).length;
    const ledgerIntentSignals = ledgerItems.filter(
      (item) =>
        item.sourceType.includes("reflection") ||
        item.sourceType.includes("coach") ||
        item.sourceType.includes("leader_chat") ||
        item.eventType.includes("insight"),
    ).length;
    const ledgerCoachSignals = ledgerItems.filter((item) => item.actorRole === "coach").length;
    const total = bucket.items.length;
    const isEmpty = total === 0;

    const evidenceBase =
      (transcriptCount * 1 + reflectionCount * 0.8 + conversationCount * 0.55 + veeraCount * 0.6 + ledgerBehaviorSignals * 0.9) /
      Math.max(total + ledgerItems.length, 1);
    const agreementBase =
      (conversationCount * 1 + reflectionCount * 0.85 + transcriptCount * 0.65 + veeraCount * 0.6 + ledgerCoachSignals * 0.95) /
      Math.max(total + ledgerItems.length, 1);
    const intentBase = (reflectionCount * 0.85 + conversationCount * 0.75 + ledgerIntentSignals * 1) / Math.max(total + ledgerItems.length, 1);
    const behaviorBase = (transcriptCount * 1 + veeraCount * 0.7 + ledgerBehaviorSignals * 1) / Math.max(total + ledgerItems.length, 1);

    const evidence = isEmpty
      ? 0.35
      : clamp(0.25 + evidenceBase * 0.6 + summaryCoverage * 0.15, 0.2, 1);
    const agreement = isEmpty
      ? 0.35
      : clamp(0.25 + agreementBase * 0.6, 0.2, 1);
    const intentSignal = isEmpty ? 0.35 : clamp(0.2 + intentBase * 0.75, 0.2, 1);
    const behaviorSignal = isEmpty ? 0.35 : clamp(0.2 + behaviorBase * 0.75, 0.2, 1);
    const alignmentGap = Math.abs(intentSignal - behaviorSignal);
    const alignment: ThroughlineArcPoint["alignment"] = alignmentGap < 0.12 ? "aligned" : alignmentGap < 0.25 ? "partial" : "gap";

    const score = evidence * 0.55 + agreement * 0.45;
    const momentum = clamp(score - previousScore, -1, 1);
    previousScore = score;
    const ySignal = clamp(score + momentum * 0.15, 0, 1);
    const y = 41 - ySignal * 22;

    const state = classifyState(evidence, agreement, momentum);
    const latest = isEmpty
      ? null
      : [...bucket.items].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())[0];

    return {
      id: `bucket-${bucket.dayKey}`,
      x: 0.5 + progress * 99,
      y,
      progress,
      evidence,
      agreement,
      intentSignal,
      behaviorSignal,
      alignment,
      momentum,
      state,
      label: format(new Date(bucket.dayKey), "EEE M/d"),
      detail: isEmpty ? "No new throughline events." : total > 1 ? `${total} milestones · ${latest?.detail || ""}` : latest?.detail || "",
      count: total,
      isEmpty,
    } satisfies ThroughlineArcPoint;
  });

  const stateCounts = points.reduce<Record<ThroughlineNodeState, number>>(
    (acc, point) => {
      if (!point.isEmpty) acc[point.state] += 1;
      return acc;
    },
    { healthy: 0, stalled: 0, risk: 0 },
  );

  return {
    points,
    effectiveWindowDays,
    sparseWindowAutoExpanded,
    stateCounts,
  };
}

export function summarizeThroughlineState(points: ThroughlineArcPoint[]) {
  const riskCount = points.filter((point) => point.state === "risk" && !point.isEmpty).length;
  const stalledCount = points.filter((point) => point.state === "stalled" && !point.isEmpty).length;
  if (riskCount > 0) return "Needs-attention pulse detected. Agreement and evidence dipped in recent periods.";
  if (stalledCount > 0) return "Needs-consistency pulse detected. Evidence is present but agreement consistency is uneven.";
  return "Momentum is building. Evidence and agreement are moving in the same direction.";
}

export function buildThroughlineGapAlert(points: ThroughlineArcPoint[], recentWindowSize = 7): ThroughlineGapAlert {
  const recent = points.filter((point) => !point.isEmpty).slice(-recentWindowSize);
  const gapDays = recent.filter((point) => point.alignment === "gap").length;
  const lowEvidenceDays = recent.filter((point) => point.evidence < THROUGHLINE_RISK_RULES.evidenceLt).length;
  const active = recent.length >= 3 && (gapDays >= 3 || lowEvidenceDays >= 3);

  return {
    active,
    recentWindowSize,
    gapDays,
    lowEvidenceDays,
  };
}

function milestoneImpactScore(item: ThroughlineMilestoneInput): number {
  const typeBase =
    item.type === "transcript" ? 1 :
    item.type === "reflection" ? 0.92 :
    item.type === "conversation" ? 0.84 :
    0.76;
  const detail = item.detail.toLowerCase();
  const riskKeywords = ["risk", "gap", "hard", "stalled", "decline", "inconsistent", "challenge"];
  const growthKeywords = ["improved", "momentum", "aligned", "summary", "progress"];
  const keywordBoost =
    (riskKeywords.some((keyword) => detail.includes(keyword)) ? 0.2 : 0) +
    (growthKeywords.some((keyword) => detail.includes(keyword)) ? 0.08 : 0);
  const linkBoost = item.link ? 0.06 : 0;
  return typeBase + keywordBoost + linkBoost;
}

export function rankThroughlineMilestones<T extends ThroughlineMilestoneInput>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const scoreDiff = milestoneImpactScore(b) - milestoneImpactScore(a);
    if (Math.abs(scoreDiff) > 0.0001) return scoreDiff > 0 ? 1 : -1;
    return new Date(b.at).getTime() - new Date(a.at).getTime();
  });
}

export function buildThroughlineDayNarrative(input: ThroughlineDayNarrativeInput): ThroughlineDayNarrative {
  if (input.isEmpty) {
    return {
      headline: "No behavior signal captured for this day.",
      summary: "No new source activity was captured this day in the selected window.",
      drivers: ["Signal was mixed without enough day-level evidence to infer a reliable pattern."],
    };
  }

  const behaviorHeadline = input.topBehaviors.length > 0
    ? `${input.topBehaviors.join(" + ")} showed up across the day.`
    : "Behavior signal captured across the day.";
  const sourceTotal =
    input.sourceCounts.transcript +
    input.sourceCounts.reflection +
    input.sourceCounts.conversation +
    input.sourceCounts.veera;
  const summary = `${input.count} source events (${sourceTotal} grouped records) contributed to this day-level behavior read. This reflects the pattern across meetings and reflections, not a one-off moment.`;

  const drivers: string[] = [];
  if (input.state === "risk") {
    drivers.push("Current day state is Needs Attention, meaning follow-through or alignment likely needs focused coaching.");
  } else if (input.state === "stalled") {
    drivers.push("Current day state is Needs Consistency, meaning consistency is flattening.");
  } else {
    drivers.push("Current day state is On Track, meaning behavior change signals are currently holding.");
  }

  if (input.evidence >= 0.68) drivers.push("Evidence density was strong across transcript and reflection sources.");
  if (input.evidence < 0.45) drivers.push("Evidence density was low, so this read has lower confidence.");
  if (input.agreement >= 0.68) drivers.push("Coach-leader alignment signals were consistent across sources.");
  if (input.agreement < 0.45) drivers.push("Coach-leader alignment looked inconsistent in the day-level record.");
  if (input.alignment === "aligned") drivers.push("What was intended and what was observed stayed aligned.");
  if (input.alignment === "partial") drivers.push("Intent and observed behavior were only partially aligned.");
  if (input.alignment === "gap") drivers.push("A notable gap emerged between intent and observed behavior.");
  if (input.momentum > 0.08) drivers.push("Momentum improved versus the prior day in this window.");
  if (input.momentum < -0.08) drivers.push("Momentum declined versus the prior day in this window.");
  if (drivers.length === 1) drivers.push("Signal was mixed without a dominant upward or downward shift.");

  return {
    headline: behaviorHeadline,
    summary,
    drivers,
  };
}
